import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";

import { cn } from "@/shared/lib";

import { formatPercent } from "../../format/formatPercent";
import type { AnalysisDeath, AnalysisMetric } from "../../model/Analysis";

const DEATHS = msg({
  id: "analysis.deaths.stat.total",
  message: "{count, plural, one {death in the match} other {deaths in the match}}",
});
const DEAD = msg({
  id: "analysis.deaths.stat.dead",
  message: "of the match dead, typical {typical}",
});
const BAD_FIGHTS = msg({
  id: "analysis.deaths.stat.bad_fights",
  message:
    "{count, plural, one {death when there was time to retreat} other {deaths when there was time to retreat}}",
});

type StatTone = "neutral" | "warn" | "bad";

const TILE: Record<StatTone, string> = {
  neutral: cn("bg-surface-raised"),
  warn: cn("border border-warn/40 bg-warn/10"),
  bad: cn("border border-bad/40 bg-bad/10"),
};

const NUMBER: Record<StatTone, string> = {
  neutral: cn("text-foreground"),
  warn: cn("text-warn"),
  bad: cn("text-bad"),
};

type DeathStatsProps = {
  deaths: readonly AnalysisDeath[];
  // Метрика «время мёртвым» из подробностей; нет — плитки нет.
  timeDead: AnalysisMetric | undefined;
};

// Три числа над списком смертей: сколько, какая доля матча мёртвым против сильных игроков
// и сколько смертей в невыгодных драках, где было время отойти.
export const DeathStats = ({ deaths, timeDead }: DeathStatsProps) => {
  const { _, i18n } = useLingui();
  const badFights = deaths.filter((death) => death.fight !== null).length;
  const deadTone: StatTone =
    timeDead?.severity === "bad" ? "bad" : timeDead?.severity === "warn" ? "warn" : "neutral";
  const tiles: { key: string; tone: StatTone; value: string; label: string }[] = [
    {
      key: "total",
      tone: "neutral",
      value: String(deaths.length),
      label: _({ ...DEATHS, values: { count: deaths.length } }),
    },
    ...(timeDead === undefined
      ? []
      : [
          {
            key: "dead",
            tone: deadTone,
            value: formatPercent(timeDead.value, i18n.locale),
            label: _({
              ...DEAD,
              values: { typical: formatPercent(timeDead.typical, i18n.locale) },
            }),
          },
        ]),
    {
      key: "bad-fights",
      tone: badFights > 0 ? "bad" : "neutral",
      value: String(badFights),
      label: _({ ...BAD_FIGHTS, values: { count: badFights } }),
    },
  ];
  return (
    <ul className="grid grid-cols-[repeat(auto-fit,minmax(min(180px,100%),1fr))] gap-2.5">
      {tiles.map((tile) => (
        <li
          key={tile.key}
          data-stat={tile.key}
          className={cn("flex flex-col rounded-[10px] px-3.5 py-3", TILE[tile.tone])}
        >
          <span className={cn("font-mono text-[22px] font-semibold", NUMBER[tile.tone])}>
            {tile.value}
          </span>
          <span className="text-[13px] text-muted">{tile.label}</span>
        </li>
      ))}
    </ul>
  );
};
